import { useRef, useState } from "react";
import { Dialog, TextField, InputAdornment } from "@mui/material";
import { RecaptchaVerifier, signInWithPhoneNumber, ConfirmationResult } from "firebase/auth";
import { firebaseAuth } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";

interface PhoneOtpModalProps {
  open: boolean;
  onClose: () => void;
  onVerified: () => void;
}

type Step = "phone" | "otp";

const PHONE_REGEX = /^\d{10}$/;
const COUNTRY_CODE_REGEX = /^\+\d{1,3}$/;
const DEFAULT_COUNTRY_CODE = "+91";
const RESEND_COOLDOWN_SECONDS = 30;
const RECAPTCHA_CONTAINER_ID = "phone-otp-recaptcha-container";

const PhoneOtpModal = ({ open, onClose, onVerified }: PhoneOtpModalProps) => {
  const { loginWithOtp } = useAuth();

  const [step, setStep] = useState<Step>("phone");
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const sendingRef = useRef(false);

  const resetState = () => {
    setStep("phone");
    setCountryCode(DEFAULT_COUNTRY_CODE);
    setPhone("");
    setOtp("");
    setEmail("");
    setError("");
    setLoading(false);
    setCooldown(0);
    confirmationResultRef.current = null;
    recaptchaVerifierRef.current?.clear();
    recaptchaVerifierRef.current = null;
    sendingRef.current = false;
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Every send/resend gets a brand new widget on a brand new DOM node.
  // Reusing one widget across attempts (even via grecaptcha.reset) races with
  // Google's own DOM manipulation once a visible challenge is involved -
  // that's what was throwing "Cannot read properties of null (reading
  // 'style')". A single-use token per attempt also avoids
  // auth/invalid-app-credential from replaying an already-consumed token.
  // The swap happens via plain DOM APIs, not React state, since React never
  // touches this element's children (grecaptcha owns them) and a state-driven
  // remount would need an extra render cycle before the new node exists.
  const getFreshRecaptchaVerifier = () => {
    recaptchaVerifierRef.current?.clear();
    recaptchaVerifierRef.current = null;

    const oldContainer = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (oldContainer?.parentNode) {
      const freshContainer = document.createElement("div");
      freshContainer.id = RECAPTCHA_CONTAINER_ID;
      oldContainer.parentNode.replaceChild(freshContainer, oldContainer);
    }

    recaptchaVerifierRef.current = new RecaptchaVerifier(
      firebaseAuth,
      RECAPTCHA_CONTAINER_ID,
      { size: "invisible" }
    );
    return recaptchaVerifierRef.current;
  };

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (sendingRef.current) return;
    setError("");
    if (!COUNTRY_CODE_REGEX.test(countryCode)) {
      setError("Enter a valid country code, e.g. +91");
      return;
    }
    if (!PHONE_REGEX.test(phone)) {
      setError("Enter a valid 10-digit phone number");
      return;
    }
    sendingRef.current = true;
    try {
      setLoading(true);
      const verifier = getFreshRecaptchaVerifier();
      confirmationResultRef.current = await signInWithPhoneNumber(
        firebaseAuth,
        `${countryCode}${phone}`,
        verifier
      );
      setStep("otp");
      startCooldown();
    } catch (err: any) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    if (!otp) {
      setError("Enter the OTP sent to your phone");
      return;
    }
    if (!confirmationResultRef.current) {
      setError("Please request a new OTP");
      return;
    }
    try {
      setLoading(true);
      const credential = await confirmationResultRef.current.confirm(otp);
      const idToken = await credential.user.getIdToken();
      await loginWithOtp(idToken, email || undefined);
      resetState();
      onVerified();
    } catch (err: any) {
      setError(err.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setStep("phone");
    setOtp("");
    setError("");
    setCooldown(0);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <div className="p-8">
        <h2 className="text-2xl font-mazzardbold mb-2 text-gray-800">
          {step === "phone" ? "Verify your number" : "Enter OTP"}
        </h2>
        <p className="text-gray-600 mb-6 text-sm">
          {step === "phone"
            ? "We need to verify your mobile number before searching."
            : `We sent a 6-digit code to ${countryCode}${phone}.`}
        </p>

        <div id={RECAPTCHA_CONTAINER_ID} />

        {step === "phone" ? (
          <>
            <TextField
              label="Mobile number"
              value={phone}
              onChange={(e) =>
                setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              fullWidth
              variant="outlined"
              sx={{ mb: 2.5 }}
              inputProps={{ inputMode: "numeric" }}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <input
                      value={countryCode}
                      onChange={(e) =>
                        setCountryCode(
                          e.target.value.replace(/[^+\d]/g, "").slice(0, 4)
                        )
                      }
                      className="w-12 text-center outline-none bg-transparent"
                      aria-label="Country code"
                    />
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}
            <button
              onClick={handleSendOtp}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </>
        ) : (
          <>
            <TextField
              label="OTP"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              fullWidth
              variant="outlined"
              sx={{ mb: 2.5 }}
              inputProps={{ inputMode: "numeric" }}
              autoFocus
            />
            <TextField
              label="Email (optional)"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ mb: 2.5 }}
            />
            {error && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}
            <button
              onClick={handleVerifyOtp}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
            <div className="flex justify-between mt-4 text-sm">
              <button
                onClick={handleChangeNumber}
                className="text-blue-800 hover:text-blue-900 font-semibold"
              >
                Change number
              </button>
              <button
                onClick={handleSendOtp}
                disabled={cooldown > 0 || loading}
                className="text-blue-800 hover:text-blue-900 font-semibold disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default PhoneOtpModal;
